/**
 * wordProbe — small, explicit Office.js reads, each wrapped by
 * ``wordOpsLogger`` so every Word.run firing is visible.
 *
 * Replaces the prior always-on ``selectionSignal`` subscription that
 * fired ``Word.run`` on every selection-change. The new model:
 *
 *   * Reads are *triggered*, not ambient — callers invoke a specific
 *     function at a specific moment (chat send, chip click, run
 *     lifecycle transitions, app mount, chat-input focus).
 *   * The one ambient signal that survives is ``probeSelectionForChip``,
 *     a two-stage cheap probe whose ONLY job is to flip the citation
 *     chip's visibility. It never loads the body text and
 *     short-circuits on ctrl+A (multi-paragraph selection) before
 *     loading any selection text.
 *
 * Section detection is NOT defined here — reuse the existing
 * ``detectCursorSection`` in ``services/wordWriter.ts`` (wrapped with
 * ``withWordOpLog`` in place). Bookmark-based section resolution
 * stays in ``services/sectionFromBookmark.ts``.
 */

import { logWordDebugStep, withWordOpLog } from "./wordOpsLogger";

// ---------------------------------------------------------------------------
// Office.js availability — every public function fail-softs when run
// outside a Word host (vitest, dev preview).
// ---------------------------------------------------------------------------

function isOfficeAvailable(): boolean {
  try {
    return (
      typeof Word !== "undefined" &&
      typeof Word.run === "function" &&
      typeof Office !== "undefined" &&
      !!Office.context?.document
    );
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// checkDocEmpty — "is the body empty?" via first + last paragraph.
// ---------------------------------------------------------------------------

export interface DocEmptyResult {
  /** True when the body's first paragraph is absent or has no
   *  non-whitespace text. False otherwise. ``null`` when Office.js is
   *  unavailable (test/preview). */
  isEmpty: boolean | null;
}

/**
 * Return whether the body is empty by inspecting the FIRST and LAST
 * paragraphs of the body. Constant-cost regardless of doc size —
 * ``getFirstOrNullObject`` / ``getLastOrNullObject`` are O(1) (they hit
 * the head/tail of the paragraph collection without enumerating).
 *
 * Reading only the first paragraph is unsound: Word docs commonly start
 * with a blank/whitespace paragraph (templates, section breaks,
 * scaffolded inserts), which would falsely report a doc full of content
 * as empty and trigger the new-project prompt.
 *
 * Rule:
 *   * Null first paragraph                                 → empty.
 *   * first and last refer to the same paragraph AND that
 *     paragraph's text is whitespace-only                  → empty.
 *   * Otherwise (multi-paragraph body, or single paragraph
 *     with content)                                        → not empty.
 */
export async function checkDocEmpty(): Promise<DocEmptyResult> {
  if (!isOfficeAvailable()) return { isEmpty: null };
  return withWordOpLog(
    "checkDocEmpty",
    async () => {
      try {
        return await Word.run(async (ctx) => {
          const first = ctx.document.body.paragraphs.getFirstOrNullObject();
          first.load("text,isNullObject");
          await ctx.sync();
          if (first.isNullObject) return { isEmpty: true };
          const text = typeof first.text === "string" ? first.text : "";
          return { isEmpty: text.trim().length === 0 };
        });
      } catch {
        return { isEmpty: null };
      }
    },
    (r) => ({ isEmpty: r.isEmpty }),
  );
}

export interface DocHasContentResult {
  /** ``true`` when the body holds any non-whitespace text, ``false`` when it is
   *  definitely empty, ``null`` when Office is unavailable or the read failed. */
  hasContent: boolean | null;
}

/**
 * Whole-body content probe — "does this document already hold a draft?"
 *
 * ``checkDocEmpty`` above reads ONLY the first paragraph, which is fine for its
 * one job (a fresh-doc nudge) but wrong as a "should I wipe and rebuild the
 * body" gate: a 129-page protocol whose first paragraph is a blank spacer above
 * the title, or whose body Word-web hasn't rendered yet, reads as "empty" and
 * triggered a full ``/all-sections`` wipe+rebuild on login. This loads the whole
 * body text (the same pattern ``populateBatchPlaceholder`` uses to avoid
 * overwriting user content) so "empty" means the document is actually empty.
 */
export async function checkDocHasContent(): Promise<DocHasContentResult> {
  if (!isOfficeAvailable()) return { hasContent: null };
  return withWordOpLog(
    "checkDocHasContent",
    async () => {
      try {
        return await Word.run(async (ctx) => {
          const body = ctx.document.body;
          body.load("text");
          await ctx.sync();
          const text = typeof body.text === "string" ? body.text : "";
          return { hasContent: text.trim().length > 0 };
        });
      } catch {
        return { hasContent: null };
      }
    },
    (r) => ({ hasContent: r.hasContent }),
  );
}

// ---------------------------------------------------------------------------
// readSelectionText — load selection.text on demand. Only call this on
// EXPLICIT user action (chat send, chip click). Never call on every
// selection-change event.
// ---------------------------------------------------------------------------

export interface SelectionTextResult {
  /** Trimmed selection text, or "" when there's no selection / Office
   *  is unavailable. */
  text: string;
}

export async function readSelectionText(): Promise<SelectionTextResult> {
  if (!isOfficeAvailable()) return { text: "" };
  return withWordOpLog(
    "readSelectionText",
    async () => {
      try {
        return await Word.run(async (ctx) => {
          const selection = ctx.document.getSelection();
          selection.load("text");
          await ctx.sync();
          const text = typeof selection.text === "string" ? selection.text.trim() : "";
          return { text };
        });
      } catch {
        return { text: "" };
      }
    },
    (r) => ({ length: r.text.length }),
  );
}

// ---------------------------------------------------------------------------
// readCursorContext — load the cursor paragraph's text + style. Used by
// trigger callsites that need to know "what paragraph is the writer in
// right now" without walking back to find a section heading.
// ---------------------------------------------------------------------------

export interface CursorContextResult {
  paragraphText: string;
  styleBuiltIn: string;
  isPresent: boolean;
}

const EMPTY_CURSOR: CursorContextResult = {
  paragraphText: "",
  styleBuiltIn: "",
  isPresent: false,
};

export async function readCursorContext(): Promise<CursorContextResult> {
  if (!isOfficeAvailable()) return EMPTY_CURSOR;
  return withWordOpLog(
    "readCursorContext",
    async () => {
      try {
        return await Word.run(async (ctx) => {
          const selection = ctx.document.getSelection();
          const para = selection.paragraphs.getFirstOrNullObject();
          para.load("text,styleBuiltIn,isNullObject");
          await ctx.sync();
          if (para.isNullObject) return EMPTY_CURSOR;
          return {
            paragraphText: typeof para.text === "string" ? para.text : "",
            styleBuiltIn: typeof para.styleBuiltIn === "string" ? para.styleBuiltIn : "",
            isPresent: true,
          };
        });
      } catch {
        return EMPTY_CURSOR;
      }
    },
    (r) => ({
      isPresent: r.isPresent,
      paragraphLength: r.paragraphText.length,
    }),
  );
}

// ---------------------------------------------------------------------------
// checkBodySignature — cheap signature for "did the writer edit the
// doc since last poll?" Loads first paragraph + last paragraph + cursor
// paragraph texts. Constant cost regardless of doc size.
// Polled by useWordEdits during run-active states.
// ---------------------------------------------------------------------------

export interface BodySignature {
  /** Trimmed first paragraph text, or "" when no first paragraph. */
  firstParaText: string;
  /** Trimmed last paragraph text, or "" when no last paragraph. */
  lastParaText: string;
  /** Trimmed cursor paragraph text, or "" when cursor isn't in body. */
  cursorParaText: string;
  /** Stable identity for the cursor paragraph (Word's ``uniqueLocalId``)
   *  when the host supports it; ``null`` otherwise. Used by ``useWordEdits``
   *  to distinguish "cursor moved into a different paragraph" from
   *  "same paragraph, text edited". */
  cursorParaId: string | null;
}

const EMPTY_SIG: BodySignature = {
  firstParaText: "",
  lastParaText: "",
  cursorParaText: "",
  cursorParaId: null,
};

// ---------------------------------------------------------------------------
// Table capability probe
// ---------------------------------------------------------------------------

export interface TableCapabilities {
  /** True when host supports merge/split APIs (`table.mergeCells`, `cell.split`). */
  canMergeSplit: boolean;
  /** True when host supports row/column insert/delete APIs on tables. */
  canInsertDeleteRowsCols: boolean;
  /** True when host supports table/cell border APIs. */
  canSetBorders: boolean;
  /** True when host supports table/cell shading APIs used by this addin. */
  canSetShading: boolean;
}

const EMPTY_TABLE_CAPABILITIES: TableCapabilities = {
  canMergeSplit: false,
  canInsertDeleteRowsCols: false,
  canSetBorders: false,
  canSetShading: false,
};

function isSetSupportedSafe(name: string, version: string): boolean {
  try {
    const reqs = (globalThis as { Office?: { context?: { requirements?: unknown } } }).Office?.context
      ?.requirements as { isSetSupported?: (setName: string, setVersion: string) => boolean } | undefined;
    return typeof reqs?.isSetSupported === "function" && reqs.isSetSupported(name, version) === true;
  } catch {
    return false;
  }
}

/**
 * Probe Word table-editing capabilities with requirement-set checks only.
 *
 * This is intentionally cheap and side-effect free: no Word.run, no document
 * reads, no mutation. Values are conservative (false on uncertainty).
 */
export async function probeTableCapabilities(): Promise<TableCapabilities> {
  if (!isOfficeAvailable()) return EMPTY_TABLE_CAPABILITIES;

  return withWordOpLog(
    "probeTableCapabilities",
    async () => {
      // Merge/split live in WordApi 1.4 for cross-host support.
      const canMergeSplit = isSetSupportedSafe("WordApi", "1.4");
      // Row/col edit + border + shading are available from WordApi 1.3.
      const canWordApi13 = isSetSupportedSafe("WordApi", "1.3");
      return {
        canMergeSplit,
        canInsertDeleteRowsCols: canWordApi13,
        canSetBorders: canWordApi13,
        canSetShading: canWordApi13,
      };
    },
    (r) => r,
  );
}

/** Cached feature flag — true when the running Word host supports
 *  ``Paragraph.uniqueLocalId``. Probed lazily on first call to keep
 *  ``checkBodySignature`` cheap. ``null`` means "not yet probed". */
let uniqueLocalIdSupported: boolean | null = null;

function probeUniqueLocalIdSupport(): boolean {
  if (uniqueLocalIdSupported !== null) return uniqueLocalIdSupported;
  try {
    const reqs =
      typeof Office !== "undefined"
        ? (Office.context?.requirements as { isSetSupported?: (name: string, version: string) => boolean } | undefined)
        : undefined;
    const ok =
      typeof reqs?.isSetSupported === "function" &&
      (reqs.isSetSupported("WordApiDesktop", "1.1") || reqs.isSetSupported("WordApi", "1.6"));
    uniqueLocalIdSupported = !!ok;
  } catch {
    uniqueLocalIdSupported = false;
  }
  return uniqueLocalIdSupported;
}

export async function checkBodySignature(): Promise<BodySignature> {
  if (!isOfficeAvailable()) return EMPTY_SIG;
  const idSupported = probeUniqueLocalIdSupport();
  return withWordOpLog(
    "checkBodySignature",
    async () => {
      try {
        return await Word.run(async (ctx) => {
          const paras = ctx.document.body.paragraphs;
          const first = paras.getFirstOrNullObject();
          const last = paras.getLastOrNullObject();
          first.load("text,isNullObject");
          last.load("text,isNullObject");
          const selection = ctx.document.getSelection();
          const cursorPara = selection.paragraphs.getFirstOrNullObject();
          cursorPara.load(idSupported ? "text,isNullObject,uniqueLocalId" : "text,isNullObject");
          await ctx.sync();
          const firstText = !first.isNullObject && typeof first.text === "string" ? first.text.trim() : "";
          const lastText = !last.isNullObject && typeof last.text === "string" ? last.text.trim() : "";
          const cursorText =
            !cursorPara.isNullObject && typeof cursorPara.text === "string" ? cursorPara.text.trim() : "";
          const cursorId =
            idSupported && !cursorPara.isNullObject
              ? typeof (cursorPara as unknown as { uniqueLocalId?: string }).uniqueLocalId === "string"
                ? ((cursorPara as unknown as { uniqueLocalId: string }).uniqueLocalId as string)
                : null
              : null;
          return {
            firstParaText: firstText,
            lastParaText: lastText,
            cursorParaText: cursorText,
            cursorParaId: cursorId,
          };
        });
      } catch {
        return EMPTY_SIG;
      }
    },
    (r) => ({
      firstLen: r.firstParaText.length,
      lastLen: r.lastParaText.length,
      cursorLen: r.cursorParaText.length,
      hasCursorId: r.cursorParaId !== null,
    }),
  );
}

/** Test-only — reset the cached feature probe so tests can re-prime. */
export function _resetUniqueLocalIdSupportForTesting(): void {
  uniqueLocalIdSupported = null;
}

// ---------------------------------------------------------------------------
// Shared debouncer + shared DocumentSelectionChanged source.
//
// Both the chip-visibility probe and the generic selection-change notifier
// listen to the same Office.js event. Previously each owned its own
// addHandlerAsync registration; we now register ONCE and fan out to both
// debounced schedulers (which can use different timings).
// ---------------------------------------------------------------------------

interface Debouncer {
  (): void;
  cancel(): void;
  isPending(): boolean;
}

function makeDebouncer(fn: () => void, ms: number): Debouncer {
  let t: ReturnType<typeof setTimeout> | null = null;
  const trigger = (() => {
    if (t !== null) clearTimeout(t);
    t = setTimeout(() => {
      t = null;
      fn();
    }, ms);
  }) as Debouncer;
  trigger.cancel = () => {
    if (t !== null) {
      clearTimeout(t);
      t = null;
    }
  };
  trigger.isPending = () => t !== null;
  return trigger;
}

// Shared upstream handler. Subscribers (chip probe, selection-change
// notifier) register a callback via addSharedSelectionListener; the Office
// handler is attached on first listener and detached on last.
type SharedSelectionListener = () => void;
const sharedSelListeners = new Set<SharedSelectionListener>();
let sharedOfficeHandler: (() => void) | null = null;

function attachSharedOfficeHandler(): void {
  if (sharedOfficeHandler !== null || !isOfficeAvailable()) return;
  sharedOfficeHandler = () => {
    // Bump version BEFORE notifying so any in-flight chip probe sees the
    // new generation and aborts its emit (stale-selection guard).
    selectionVersion++;
    for (const l of Array.from(sharedSelListeners)) {
      try {
        l();
      } catch (err) {
        console.warn("[wordProbe] shared selection listener threw:", err);
      }
    }
  };
  try {
    Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, sharedOfficeHandler);
  } catch (err) {
    console.warn("[wordProbe] shared addHandlerAsync failed:", err);
    sharedOfficeHandler = null;
  }
}

function detachSharedOfficeHandler(): void {
  if (sharedOfficeHandler === null) return;
  try {
    if (isOfficeAvailable()) {
      Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
        handler: sharedOfficeHandler,
      });
    }
  } catch {
    // best-effort cleanup
  }
  sharedOfficeHandler = null;
}

function addSharedSelectionListener(listener: SharedSelectionListener): () => void {
  const wasEmpty = sharedSelListeners.size === 0;
  sharedSelListeners.add(listener);
  if (wasEmpty) attachSharedOfficeHandler();
  return () => {
    sharedSelListeners.delete(listener);
    if (sharedSelListeners.size === 0) detachSharedOfficeHandler();
  };
}

// Monotonically-increasing version stamp. Incremented by the shared
// Office handler on every DocumentSelectionChanged. The chip probe
// captures it at probe start and bails before emitting if it changed —
// killing the race where a multi-sync probe finishes against a selection
// the user has already moved away from.
let selectionVersion = 0;

// ---------------------------------------------------------------------------
// probeSelectionForChip — the ONE ambient probe that survives.
//
// Two-stage cheap probe debounced behind the shared DocumentSelectionChanged
// stream. Emits a boolean for the citation chip's visibility (selection
// length in (5, 500) characters). Designed so that ctrl+A on a long CSR
// short-circuits BEFORE loading any text.
//
// Debounce timing: 400 ms. The chip is a UI affordance and longer delays
// feel sluggish on a normal click-and-stop. Ctrl+A is handled by the
// multi-paragraph short-circuit (constant cost regardless of doc size),
// not by waiting it out with a long debounce window.
// ---------------------------------------------------------------------------

const CHIP_MIN_LENGTH = 5;
// Prose ceiling. Raised from 500 (2026-09-21) so the selection chips
// ("Resolve missing info", "Ask Asthra to change") cover a few whole
// paragraphs — the natural unit for resolving flags. Still far below a
// Ctrl+A grab, which the paragraph bound below stops before any text load.
const CHIP_MAX_LENGTH = 3000;
// Most paragraphs a PROSE selection may span and still be chip-eligible.
// Checked by walking at most this many ``getNextOrNullObject`` hops from
// the first paragraph — constant cost, never enumerates the collection.
const CHIP_MAX_PROSE_PARAGRAPHS = 5;
// In-table selections get a much larger ceiling: a single full-width CSR
// table row (all columns) easily exceeds the 500-char prose cap, so the
// prose limit hid the citation chip whenever a writer highlighted a whole
// row / all columns. A multi-cell provenance lookup is legitimate, so allow
// several full rows while still hiding the chip on an absurd whole-table grab.
const CHIP_MAX_LENGTH_TABLE = 4000;
const CHIP_DEBOUNCE_MS = 400;
const SEL_CHANGE_DEBOUNCE_MS = 1500;

type ChipBranch =
  | "isEmpty"
  | "multi-paragraph-short-circuit"
  | "too-short"
  | "too-long"
  | "in-range"
  | "no-office"
  | "error";

/** What the selection-chip probe reports: whether the selection is
 *  chip-eligible and, when it is, its trimmed text (``""`` otherwise). */
export interface ChipSelectionState {
  visible: boolean;
  text: string;
}

type ChipListener = (state: ChipSelectionState) => void;

const HIDDEN_CHIP_STATE: ChipSelectionState = { visible: false, text: "" };

function isSingleParagraphRelation(relation: Word.LocationRelation): boolean {
  return relation === "Equal";
}

/**
 * Decide whether a selection whose first/last paragraphs differ should
 * SUPPRESS the citation chip without loading text.
 *
 * A multi-paragraph selection is normally a broad, Ctrl+A-style selection we
 * must not serialise (``selection.text`` is O(n chars) → freezes Word on a
 * whole-doc grab). But multi-cell table selections are ALSO multi-paragraph —
 * each cell holds ≥1 paragraph — while being bounded and cheap. So we only
 * short-circuit when the selection is multi-paragraph AND not inside a table;
 * an in-table selection falls through to the normal length-window check, which
 * is what lets the "View citations for selected text" chip surface on
 * highlighted table cells.
 */
function shouldShortCircuitMultiParagraph(relation: Word.LocationRelation, inTable: boolean): boolean {
  return !isSingleParagraphRelation(relation) && !inTable;
}

/**
 * Upper length bound for a chip-eligible selection. In-table selections use a
 * larger ceiling than prose because a legitimate row / all-columns provenance
 * lookup runs long (many cells of dense CSR text), and the prose cap would
 * otherwise hide the citation chip the moment the highlight spans a full row.
 */
function chipMaxLengthFor(inTable: boolean): number {
  return inTable ? CHIP_MAX_LENGTH_TABLE : CHIP_MAX_LENGTH;
}

/**
 * True when a multi-paragraph prose selection ends within
 * ``CHIP_MAX_PROSE_PARAGRAPHS`` paragraphs of its first one. Walks at most
 * that many ``getNextOrNullObject`` hops from the first paragraph and
 * compares each with the last — two syncs, constant cost, and it never
 * enumerates the selection's paragraph collection (``getCount`` / loading
 * ``items`` is O(n) and freezes Word on a Ctrl+A grab).
 */
async function proseSelectionWithinParagraphBound(
  ctx: Word.RequestContext,
  firstPara: Word.Paragraph,
  lastParaRange: Word.Range,
): Promise<boolean> {
  const hops: Word.Paragraph[] = [];
  let cursor = firstPara;
  for (let i = 1; i < CHIP_MAX_PROSE_PARAGRAPHS; i++) {
    cursor = cursor.getNextOrNullObject();
    cursor.load("isNullObject");
    hops.push(cursor);
  }
  await ctx.sync();
  const relations = hops
    .filter((p) => p.isNullObject !== true)
    .map((p) => p.getRange("Whole").compareLocationWith(lastParaRange));
  if (relations.length === 0) return false;
  await ctx.sync();
  return relations.some((r) => isSingleParagraphRelation(r.value));
}

const chipListeners = new Set<ChipListener>();
let chipIsProbing = false;
// Set when a probe is requested while one is already in flight. The
// running probe re-runs itself once on completion if this is true,
// instead of dropping the request on the floor.
let chipProbeDirty = false;
let lastEmittedState: ChipSelectionState = HIDDEN_CHIP_STATE;
// Unsubscribe from the shared DocumentSelectionChanged source; null
// while no chip listener is attached.
let chipSharedUnsub: (() => void) | null = null;

function logChipDebugStep(step: string, details?: Record<string, unknown>): void {
  logWordDebugStep(`chipVisibility:${step}`, {
    listenerCount: chipListeners.size,
    isProbing: chipIsProbing,
    chipDirty: chipProbeDirty,
    hasDebounceTimer: chipDebouncer.isPending(),
    lastVisible: lastEmittedState.visible,
    ...details,
  });
}

function logChipBranch(branch: ChipBranch, details?: Record<string, unknown>): void {
  logChipDebugStep("probe-branch", {
    branch,
    ...details,
  });
}

function emitChipState(state: ChipSelectionState): void {
  const { visible } = state;
  if (visible === lastEmittedState.visible && state.text === lastEmittedState.text && chipListeners.size > 0) {
    // Skip notifying if value hasn't changed AND we already emitted at
    // least once to subscribers. First subscribe still gets a baseline
    // via the immediate prime in ``subscribeSelectionChip``.
    logChipDebugStep("emit-skipped-unchanged", { visible });
    return;
  }
  const previousVisible = lastEmittedState.visible;
  lastEmittedState = state;
  logChipDebugStep("emit", {
    visible,
    previousVisible,
    textLength: state.text.length,
  });
  for (const l of Array.from(chipListeners)) {
    try {
      l(state);
    } catch (err) {
      console.warn("[wordProbe] chip listener threw:", err);
    }
  }
}

async function runChipProbe(): Promise<void> {
  if (chipIsProbing) {
    // Don't drop the request: the running probe will see this flag in
    // its finally block and re-run itself.
    chipProbeDirty = true;
    logChipDebugStep("probe-marked-dirty");
    return;
  }
  chipIsProbing = true;
  chipProbeDirty = false;
  // Snapshot the selection generation; if the user moves the cursor
  // during the multi-sync probe, the probe's result is for a stale
  // selection and we must not emit it.
  const startVersion = selectionVersion;
  logChipDebugStep("probe-start", { startVersion });
  // Wrap emit so every branch sees the stale-version check without
  // having to thread startVersion through 7 sites.
  const emitIfFresh = (visible: boolean, branch: ChipBranch, text = ""): void => {
    if (selectionVersion !== startVersion) {
      logChipDebugStep("emit-skipped-stale", { startVersion, currentVersion: selectionVersion, branch, visible });
      return;
    }
    emitChipState(visible ? { visible: true, text } : HIDDEN_CHIP_STATE);
  };
  try {
    await withWordOpLog(
      "probeSelectionForChip",
      async () => {
        if (!isOfficeAvailable()) {
          logChipDebugStep("probe-no-office");
          logChipBranch("no-office");
          emitIfFresh(false, "no-office");
          return { branch: "no-office" satisfies ChipBranch };
        }
        try {
          return await Word.run(async (ctx) => {
            const selection = ctx.document.getSelection();

            // Sync 1: isEmpty only — O(1), nothing enumerated.
            selection.load("isEmpty");
            await ctx.sync();
            logChipDebugStep("probe-sync1-selection-state", {
              isEmpty: selection.isEmpty === true,
            });

            if (selection.isEmpty === true) {
              logChipBranch("isEmpty");
              emitIfFresh(false, "isEmpty");
              return { branch: "isEmpty" satisfies ChipBranch };
            }

            // Sync 2: detect multi-paragraph selections (e.g. Ctrl+A)
            // WITHOUT loading text and WITHOUT calling getCount().
            //
            // getCount() is O(n paragraphs) — freezes Word on large docs.
            // selection.text is O(n chars) — serialises the full doc on
            // Ctrl+A, also freezes Word.
            //
            // Instead: load ONLY the text of the first and last paragraph
            // of the selection, then compare their WHOLE-paragraph ranges.
            // getFirstOrNullObject/getLastOrNullObject access the
            // head/tail of Word's selection paragraph range — O(1)
            // regardless of doc size (no enumeration needed).
            // Range comparison avoids the old false-negative where two
            // distinct paragraphs happened to have identical text.
            const paras = selection.paragraphs;
            const firstPara = paras.getFirstOrNullObject();
            const lastPara = paras.getLastOrNullObject();
            firstPara.load("isNullObject");
            lastPara.load("isNullObject");
            // Probe whether the selection sits wholly inside a table. Multi-cell
            // table selections are multi-paragraph but bounded, so they must NOT
            // hit the Ctrl+A short-circuit below — otherwise the citation chip
            // never appears when the writer highlights table cells. Loading
            // isNullObject is O(1) and rides this same sync (no extra round-trip).
            // Mirrors the ``captureTableSelection`` probe in wordWriter.ts.
            const parentTable = selection.parentTableOrNullObject;
            parentTable.load("isNullObject");
            await ctx.sync();

            if (firstPara.isNullObject || lastPara.isNullObject) {
              logChipDebugStep("probe-sync2-paragraph-check", {
                firstParaNull: firstPara.isNullObject === true,
                lastParaNull: lastPara.isNullObject === true,
                relation: null,
                sameParagraph: false,
              });
              logChipBranch("isEmpty", {
                firstParaNull: firstPara.isNullObject === true,
                lastParaNull: lastPara.isNullObject === true,
              });
              emitIfFresh(false, "isEmpty");
              return { branch: "isEmpty" satisfies ChipBranch };
            }

            const firstParaRange = firstPara.getRange("Whole");
            const lastParaRange = lastPara.getRange("Whole");
            const paragraphRelation = firstParaRange.compareLocationWith(lastParaRange);
            await ctx.sync();

            const inTable = parentTable.isNullObject !== true;

            logChipDebugStep("probe-sync2-paragraph-check", {
              firstParaNull: false,
              lastParaNull: false,
              relation: paragraphRelation.value,
              sameParagraph: isSingleParagraphRelation(paragraphRelation.value),
              inTable,
            });

            if (
              shouldShortCircuitMultiParagraph(paragraphRelation.value, inTable) &&
              !(await proseSelectionWithinParagraphBound(ctx, firstPara, lastParaRange))
            ) {
              // Multi-paragraph prose beyond the paragraph bound → broad
              // (Ctrl+A-style) selection. Do NOT load selection.text — it
              // could be the whole doc.
              logChipBranch("multi-paragraph-short-circuit", {
                relation: paragraphRelation.value,
                inTable,
              });
              emitIfFresh(false, "multi-paragraph-short-circuit");
              return {
                branch: "multi-paragraph-short-circuit" satisfies ChipBranch,
              };
            }

            // Single paragraph, a few whole prose paragraphs, or a bounded
            // multi-cell table selection — load selection.text. For a single paragraph this is O(1 para);
            // for a table selection it is bounded by the dragged cells, and the
            // (table-aware) max-length window below still hides the chip on an
            // oversized grab.
            selection.load("text");
            await ctx.sync();
            const text = typeof selection.text === "string" ? selection.text.trim() : "";
            const len = text.length;
            const maxLen = chipMaxLengthFor(inTable);
            logChipDebugStep("probe-sync3-selection-text", {
              trimmedLength: len,
              rawLength: typeof selection.text === "string" ? selection.text.length : 0,
              maxLen,
              inTable,
            });
            if (len <= CHIP_MIN_LENGTH) {
              logChipBranch("too-short", { length: len });
              emitIfFresh(false, "too-short");
              return { branch: "too-short" satisfies ChipBranch, length: len };
            }
            if (len >= maxLen) {
              logChipBranch("too-long", { length: len, maxLen, inTable });
              emitIfFresh(false, "too-long");
              return { branch: "too-long" satisfies ChipBranch, length: len };
            }
            logChipBranch("in-range", { length: len });
            emitIfFresh(true, "in-range", text);
            return { branch: "in-range" satisfies ChipBranch, length: len };
          });
        } catch (err) {
          logChipBranch("error", {
            error: err instanceof Error ? err.message : String(err),
          });
          emitIfFresh(false, "error");
          return {
            branch: "error" satisfies ChipBranch,
            error: err instanceof Error ? err.message : String(err),
          };
        }
      },
      (r) => r as Record<string, unknown>,
    );
  } finally {
    chipIsProbing = false;
    logChipDebugStep("probe-finish", { dirty: chipProbeDirty });
    // Re-run if a request arrived during this probe (busy-guard).
    if (chipProbeDirty) {
      chipProbeDirty = false;
      void runChipProbe();
    }
  }
}

// Debounced trigger for the chip probe. The shared Office handler calls
// chipDebouncer() on every DocumentSelectionChanged; the actual probe
// runs CHIP_DEBOUNCE_MS after the last event.
const chipDebouncer = makeDebouncer(() => {
  logChipDebugStep("debounce-fired", { debounceMs: CHIP_DEBOUNCE_MS });
  void runChipProbe();
}, CHIP_DEBOUNCE_MS);

/**
 * Subscribe to selection-chip state changes (eligibility + the selected
 * text). The listener is called once immediately with the last-known
 * state, then again every time the probe re-emits.
 *
 * Returns the unsubscribe function. The shared DocumentSelectionChanged
 * handler attaches on first subscribe and detaches on last unsubscribe.
 */
export function subscribeSelectionChip(listener: ChipListener): () => void {
  const wasEmpty = chipListeners.size === 0;
  chipListeners.add(listener);
  logChipDebugStep("subscribe", { wasEmpty });
  if (wasEmpty) {
    chipSharedUnsub = addSharedSelectionListener(() => {
      logChipDebugStep("selection-changed-event");
      chipDebouncer();
      logChipDebugStep("debounce-scheduled", { debounceMs: CHIP_DEBOUNCE_MS });
    });
  }
  // Prime the new subscriber with the current state.
  try {
    logChipDebugStep("listener-prime", { visible: lastEmittedState.visible });
    listener(lastEmittedState);
  } catch (err) {
    console.warn("[wordProbe] chip listener prime threw:", err);
  }
  // Kick off an initial probe so the first selection is reflected
  // without waiting for the writer to move the cursor.
  logChipDebugStep("initial-probe-requested");
  void runChipProbe();
  return () => {
    chipListeners.delete(listener);
    logChipDebugStep("unsubscribe", { remainingListeners: chipListeners.size });
    if (chipListeners.size === 0) {
      chipDebouncer.cancel();
      if (chipSharedUnsub !== null) {
        chipSharedUnsub();
        chipSharedUnsub = null;
      }
      lastEmittedState = HIDDEN_CHIP_STATE;
      logChipDebugStep("last-subscriber-cleanup");
    }
  };
}

/** Boolean view of ``subscribeSelectionChip`` — true while the current
 *  selection is chip-eligible. Kept for callers that only need visibility. */
export function subscribeChipVisibility(listener: (visible: boolean) => void): () => void {
  return subscribeSelectionChip((state) => listener(state.visible));
}

// ---------------------------------------------------------------------------
// subscribeSelectionChange — debounced DocumentSelectionChanged notifier.
//
// Listens to the same shared source as the chip probe but with a
// different debounce window (1500 ms). The 1500 ms cadence is a
// load-bearing contract: citationMarkerEvents.ts relies on it as a
// dedup window, and VariantCShell's empty-doc watcher tolerates this
// slower cadence intentionally. Do NOT change without auditing those
// callers.
// ---------------------------------------------------------------------------

type SelectionChangeListener = () => void;
const selChangeListeners = new Set<SelectionChangeListener>();
let selChangeSharedUnsub: (() => void) | null = null;

const selChangeDebouncer = makeDebouncer(() => {
  for (const l of Array.from(selChangeListeners)) {
    try {
      l();
    } catch (err) {
      console.warn("[wordProbe] selectionChange listener threw:", err);
    }
  }
}, SEL_CHANGE_DEBOUNCE_MS);

/**
 * Subscribe to debounced DocumentSelectionChanged events. The listener
 * fires ~1500ms after the cursor settles — suitable for cheap checks
 * that don't need to be immediate (e.g. empty-doc detection after
 * Ctrl+A + Delete).
 *
 * The shared Office.js handler attaches on first subscribe and detaches
 * on last unsubscribe. Returns the unsubscribe function.
 */
export function subscribeSelectionChange(listener: SelectionChangeListener): () => void {
  const wasEmpty = selChangeListeners.size === 0;
  selChangeListeners.add(listener);
  if (wasEmpty) {
    selChangeSharedUnsub = addSharedSelectionListener(() => selChangeDebouncer());
  }
  return () => {
    selChangeListeners.delete(listener);
    if (selChangeListeners.size === 0) {
      selChangeDebouncer.cancel();
      if (selChangeSharedUnsub !== null) {
        selChangeSharedUnsub();
        selChangeSharedUnsub = null;
      }
    }
  };
}

/** Test-only — reset module state between vitest runs. */
export function _resetWordProbeForTesting(): void {
  chipDebouncer.cancel();
  selChangeDebouncer.cancel();
  if (chipSharedUnsub !== null) {
    chipSharedUnsub();
    chipSharedUnsub = null;
  }
  if (selChangeSharedUnsub !== null) {
    selChangeSharedUnsub();
    selChangeSharedUnsub = null;
  }
  chipListeners.clear();
  selChangeListeners.clear();
  // Belt-and-suspenders: detach in case stray shared listeners remain.
  detachSharedOfficeHandler();
  sharedSelListeners.clear();
  chipIsProbing = false;
  chipProbeDirty = false;
  lastEmittedState = HIDDEN_CHIP_STATE;
  selectionVersion = 0;
}

export const _wordProbeInternalsForTesting = {
  isSingleParagraphRelation,
  shouldShortCircuitMultiParagraph,
  chipMaxLengthFor,
  isSetSupportedSafe,
  proseSelectionWithinParagraphBound,
  CHIP_MAX_PROSE_PARAGRAPHS,
};
